import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Icon } from '@iconify/react'
import Box from '@mui/material/Box'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import { lightAt, sunTimes, type Light } from '@jupi/shared'
import { useClubLocation } from '../clubLocation'
import { useServerClock } from '../hooks/useServerClock'
import { formatClockTime, formatUtcOffset, timezoneLabel } from '../format'

// The header clock, ported from mik-ng's ClockDisplay.
//
// Two readings of the same instant: UTC, which is what a flight plan, a
// NOTAM and every logged time are written in, and the club's local
// wall-clock, which is what a booking is made in. The tick is
// server-authoritative (see useServerClock) -- and when the device's own
// clock disagrees by more than a few minutes, the warning says so rather
// than letting a member read a plausible wrong time.
//
// A third line appears when the member's device is in neither zone, which
// is exactly when they most need it: away on a trip.
// Icon names are whole literals so the build bundles them.
const LIGHT_ICON: Record<Light, string> = {
  day: 'mdi:white-balance-sunny',
  twilight: 'mdi:weather-sunset',
  night: 'mdi:weather-night',
}

// Day, twilight or night at the club's field right now, with the sun's times
// for today in the tooltip. Offline: from sunTimes, no web service.
function DaylightIcon({ utcMs, timezone, synced }: { utcMs: number; timezone: string; synced: boolean }) {
  const { t } = useTranslation()
  const place = useClubLocation()
  const light = lightAt(utcMs, place)
  // The club-local calendar day, YYYY-MM-DD.
  const dayKey = new Intl.DateTimeFormat('en-CA', { timeZone: timezone, year: 'numeric', month: '2-digit', day: '2-digit' }).format(utcMs)
  const today = useMemo(() => sunTimes(dayKey, place.lat, place.lon), [dayKey, place])
  const at = (iso: string) => formatClockTime(iso, timezone)
  const { sun, civilTwilight } = today

  const lines = [t(`clock.${light}Now`)]
  if (sun.state === 'normal') {
    const minutes = Math.round((Date.parse(sun.set!) - Date.parse(sun.rise!)) / 60_000)
    lines.push(
      t('clock.sun', { rise: at(sun.rise!), set: at(sun.set!) }),
      t('clock.daylight', { hours: Math.floor(minutes / 60), minutes: minutes % 60 }),
    )
  } else {
    lines.push(t(sun.state === 'alwaysUp' ? 'clock.midnightSun' : 'clock.polarNight'))
  }
  if (civilTwilight.state === 'normal') {
    lines.push(
      t('clock.nightEnds', { time: at(civilTwilight.rise!) }),
      t('clock.nightBegins', { time: at(civilTwilight.set!) }),
    )
  } else if (civilTwilight.state === 'alwaysUp') {
    lines.push(t('clock.noNight'))
  }
  lines.push(t('clock.at', { place: place.code, zone: timezoneLabel(timezone) }))

  return (
    <Tooltip
      arrow
      title={
        <Box>
          {lines.map((line) => (
            <Box key={line}>{line}</Box>
          ))}
        </Box>
      }
    >
      <Box component="span" sx={{ display: 'flex', cursor: 'help', opacity: synced ? 0.9 : 0.35 }} aria-label={lines[0]}>
        <Icon icon={LIGHT_ICON[light]} width={22} />
      </Box>
    </Tooltip>
  )
}

export function ClockDisplay({ showSeconds = false }: { showSeconds?: boolean }) {
  const { t } = useTranslation()
  const { utcMs, timezone, synced, skewed, skewMs } = useServerClock()

  const deviceZone = Intl.DateTimeFormat().resolvedOptions().timeZone
  const clubOffset = formatUtcOffset(utcMs, timezone)
  const deviceOffset = formatUtcOffset(utcMs, deviceZone)
  // Compared by offset rather than by zone name: Stockholm and Helsinki are
  // different zones, but a clock showing both would show the same digits.
  const deviceIsElsewhere = deviceOffset !== clubOffset && deviceOffset !== 'UTC'

  const skewMinutes = Math.round(Math.abs(skewMs) / 60000)
  const skewTitle = t('clock.skew', {
    minutes: skewMinutes,
    direction: t(skewMs > 0 ? 'clock.behind' : 'clock.ahead'),
  })

  const line = (label: string, time: string, selected = false) => (
    <Typography
      variant="caption"
      sx={{
        fontFamily: 'monospace',
        fontSize: '0.68rem',
        lineHeight: 1.4,
        letterSpacing: 0,
        color: 'inherit',
        opacity: synced ? 0.75 : 0.35,
        whiteSpace: 'nowrap',
      }}
    >
      {selected ? <Icon icon="mdi:check" style={{ marginRight: 6, verticalAlign: '-2px' }} /> : null}
      <Box component="span" sx={selected ? { fontWeight: 700, opacity: 1 } : undefined}>
        {label}
      </Box>
      &nbsp;
      {/* Placeholder rather than the device clock before the first sync:
          showing an unverified time as if it were the server's is the one
          thing this component exists to prevent. */}
      {synced ? time : '--:--'}
    </Typography>
  )

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      {skewed ? (
        <Tooltip title={skewTitle} arrow>
          <Box component="span" sx={{ display: 'flex', cursor: 'help' }}>
            <Icon icon="mdi:clock-alert-outline" width={16} color="#ed6c02" />
          </Box>
        </Tooltip>
      ) : null}

      {synced ? <DaylightIcon utcMs={utcMs} timezone={timezone} synced={synced} /> : null}

      <Tooltip title={t('clock.shownIn', { zone: timezone })} arrow>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1 }}>
          {line('UTC', formatClockTime(utcMs, 'UTC', showSeconds))}

          {deviceIsElsewhere
            ? line(deviceOffset, formatClockTime(utcMs, deviceZone, showSeconds))
            : null}

          {/* The club's own time, ticked and checked: it is the zone every
              booking on the page is written in. */}
          {line(clubOffset, formatClockTime(utcMs, timezone, showSeconds), true)}

          <Typography
            variant="caption"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.55rem',
              lineHeight: 1.2,
              color: 'inherit',
              opacity: synced ? 0.55 : 0.25,
              display: { xs: 'none', sm: 'block' },
            }}
          >
            {timezoneLabel(timezone)}
          </Typography>
        </Box>
      </Tooltip>
    </Box>
  )
}
